import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Heart, Loader2, AlertCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { usePatientStore } from '../stores/patient';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { KoyoBranding } from '../components/KoyoBranding';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { RadioGroup, RadioGroupItem } from '../components/ui/RadioGroup';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface FormData {
  firstName: string;
  lastName: string;
  age: string;
  gender: string;
  phone: string;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  age?: string;
  gender?: string;
  locationId?: string;
}

interface Location {
  id: string;
  name: string;
  address?: string;
}

export function Registration() {
  const navigate = useNavigate();
  const { setPatient, setQueueEntry, setLocationId, locationId } = usePatientStore();
  const { isOnline } = useOnlineStatus();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    phone: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // Fetch locations on mount, or use env variable if set
  useEffect(() => {
    const envLocationId = import.meta.env.VITE_LOCATION_ID;
    if (envLocationId && envLocationId !== 'default-location') {
      setLocationId(envLocationId);
      setLocationsLoading(false);
      return;
    }

    async function fetchLocations() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations`);
        if (response.ok) {
          const json = await response.json();
          const data = json.data || json;
          const locs = Array.isArray(data) ? data : (data.locations || []);
          setLocations(locs.filter((l: Location & { isActive?: boolean }) => l.isActive !== false));
          // If only one location, auto-select it
          if (locs.length === 1) {
            setLocationId(locs[0].id);
          }
        }
      } catch {
        // Will show error when submitting
      } finally {
        setLocationsLoading(false);
      }
    }

    fetchLocations();
  }, [setLocationId]);

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.age) {
      errors.age = 'Age is required';
    } else {
      const age = parseInt(formData.age, 10);
      if (isNaN(age) || age < 1 || age > 120) {
        errors.age = 'Please enter a valid age (1-120)';
      }
    }

    if (!formData.gender) {
      errors.gender = 'Please select your gender';
    }

    if (!locationId || locationId === 'default-location') {
      errors.locationId = 'Please select a location';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    // Check online status
    if (!isOnline) {
      toast.error('No internet connection. Please try again when online.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Register patient and join queue
      const response = await fetch(`${API_BASE_URL}/api/v1/patients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          age: parseInt(formData.age, 10),
          gender: formData.gender,
          phoneNumber: formData.phone.trim() || undefined,
          locationId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle validation errors from server
        if (response.status === 400 && errorData.error?.details) {
          const serverErrors: ValidationErrors = {};
          const details = errorData.error.details;

          // Backend sends details as array: [{path, message}]
          if (Array.isArray(details)) {
            for (const d of details) {
              const field = d.path as keyof ValidationErrors;
              if (field) serverErrors[field] = d.message;
            }
          } else {
            // Also handle object format for backwards compatibility
            if (details.firstName) serverErrors.firstName = details.firstName;
            if (details.lastName) serverErrors.lastName = details.lastName;
            if (details.age) serverErrors.age = details.age;
            if (details.gender) serverErrors.gender = details.gender;
            if (details.locationId) serverErrors.locationId = details.locationId;
          }

          if (Object.keys(serverErrors).length > 0) {
            setValidationErrors(serverErrors);
            toast.error('Please correct the errors in the form');
            return;
          }
        }

        throw new Error(errorData.error?.message || 'Failed to register. Please try again.');
      }

      const json = await response.json();
      const data = json.data || json;

      // Store patient data
      setPatient({
        id: data.patient.id,
        firstName: data.patient.firstName,
        lastName: data.patient.lastName,
        age: data.patient.age,
        gender: data.patient.gender,
        phoneNumber: data.patient.phoneNumber,
      });

      // Store queue entry — data.position may be a QueuePosition object
      const pos = data.position;
      const numericPosition = typeof pos === 'object' ? pos.position : pos;
      const waitMinutes = typeof pos === 'object' ? pos.estimatedWaitMinutes : 0;

      setQueueEntry({
        id: data.queueEntry.id,
        position: data.queueEntry.position ?? numericPosition ?? 0,
        estimatedWaitMinutes: data.queueEntry.estimatedWaitMinutes ?? waitMinutes ?? 0,
        status: data.queueEntry.status,
        enteredAt: data.queueEntry.enteredAt,
      });

      toast.success('Successfully joined the queue!');
      navigate('/queue');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear field error when user types
  const handleFieldChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors({ ...validationErrors, [field]: undefined });
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-2xl flex-1 flex flex-col justify-center">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-emerald-600 p-3 rounded-2xl">
              <Heart className="size-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl text-white mb-2">Talk to a Doctor</h1>
          <p className="text-xl text-slate-400">Please register to join the queue</p>
        </div>

        {/* Offline Warning */}
        {!isOnline && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl flex items-center gap-3">
            <AlertCircle className="size-6 text-red-400 flex-shrink-0" />
            <p className="text-red-400">No internet connection. Please connect to continue.</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
            <p className="text-red-400 text-center">{error}</p>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="bg-[#1e293b] rounded-3xl p-8 shadow-2xl border border-slate-700">
          <div className="space-y-6">
            {/* First Name */}
            <div>
              <Label htmlFor="firstName" className="text-slate-200 text-lg mb-2 block">
                First Name *
              </Label>
              <Input
                id="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                className={`h-16 text-lg bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.firstName ? 'border-red-500' : ''
                }`}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {validationErrors.firstName && (
                <p className="text-red-400 text-sm mt-2">{validationErrors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="lastName" className="text-slate-200 text-lg mb-2 block">
                Last Name *
              </Label>
              <Input
                id="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                className={`h-16 text-lg bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.lastName ? 'border-red-500' : ''
                }`}
                placeholder="Enter your last name"
                disabled={isSubmitting}
              />
              {validationErrors.lastName && (
                <p className="text-red-400 text-sm mt-2">{validationErrors.lastName}</p>
              )}
            </div>

            {/* Age */}
            <div>
              <Label htmlFor="age" className="text-slate-200 text-lg mb-2 block">
                Age *
              </Label>
              <Input
                id="age"
                type="number"
                required
                min="1"
                max="120"
                value={formData.age}
                onChange={(e) => handleFieldChange('age', e.target.value)}
                className={`h-16 text-lg bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.age ? 'border-red-500' : ''
                }`}
                placeholder="Enter your age"
                disabled={isSubmitting}
              />
              {validationErrors.age && (
                <p className="text-red-400 text-sm mt-2">{validationErrors.age}</p>
              )}
            </div>

            {/* Gender */}
            <div>
              <Label className="text-slate-200 text-lg mb-3 block">Gender *</Label>
              <RadioGroup
                value={formData.gender}
                onValueChange={(value) => handleFieldChange('gender', value)}
                className="grid grid-cols-2 gap-4"
                disabled={isSubmitting}
              >
                <div>
                  <RadioGroupItem
                    value="male"
                    id="male"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="male"
                    className={`flex items-center justify-center h-16 rounded-xl border-2 bg-slate-800 text-slate-200 cursor-pointer transition-all peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/20 peer-data-[state=checked]:text-blue-400 hover:border-slate-500 ${
                      validationErrors.gender ? 'border-red-500' : 'border-slate-600'
                    }`}
                  >
                    <span className="text-lg">Male</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem
                    value="female"
                    id="female"
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor="female"
                    className={`flex items-center justify-center h-16 rounded-xl border-2 bg-slate-800 text-slate-200 cursor-pointer transition-all peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-500/20 peer-data-[state=checked]:text-blue-400 hover:border-slate-500 ${
                      validationErrors.gender ? 'border-red-500' : 'border-slate-600'
                    }`}
                  >
                    <span className="text-lg">Female</span>
                  </Label>
                </div>
              </RadioGroup>
              {validationErrors.gender && (
                <p className="text-red-400 text-sm mt-2">{validationErrors.gender}</p>
              )}
            </div>

            {/* Phone (Optional) */}
            <div>
              <Label htmlFor="phone" className="text-slate-200 text-lg mb-2 block">
                Phone Number (Optional)
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-16 text-lg bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500"
                placeholder="+234 800 000 0000"
                disabled={isSubmitting}
              />
            </div>

            {/* Location Selection - shown only when VITE_LOCATION_ID is not set */}
            {locations.length > 1 && (
              <div>
                <Label className="text-slate-200 text-lg mb-3 block">Location *</Label>
                <div className="grid gap-3">
                  {locations.map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => {
                        setLocationId(loc.id);
                        if (validationErrors.locationId) {
                          setValidationErrors({ ...validationErrors, locationId: undefined });
                        }
                      }}
                      className={`flex items-center gap-3 h-16 px-4 rounded-xl border-2 bg-slate-800 text-left transition-all ${
                        locationId === loc.id
                          ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                          : validationErrors.locationId
                          ? 'border-red-500 text-slate-200 hover:border-slate-500'
                          : 'border-slate-600 text-slate-200 hover:border-slate-500'
                      }`}
                      disabled={isSubmitting}
                    >
                      <MapPin className="size-5 flex-shrink-0" />
                      <span className="text-lg">{loc.name}</span>
                    </button>
                  ))}
                </div>
                {validationErrors.locationId && (
                  <p className="text-red-400 text-sm mt-2">{validationErrors.locationId}</p>
                )}
              </div>
            )}

            {/* Location loading state */}
            {locationsLoading && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="size-5 animate-spin text-slate-400" />
                <span className="text-slate-400">Loading locations...</span>
              </div>
            )}

            {/* No locations available */}
            {!locationsLoading && locations.length === 0 && (!locationId || locationId === 'default-location') && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
                <p className="text-red-400 text-center">No locations available. Please contact support.</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-20 text-2xl bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg transition-all duration-200 hover:shadow-xl mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || !isOnline || locationsLoading}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-3">
                  <Loader2 className="size-6 animate-spin" />
                  Joining Queue...
                </span>
              ) : (
                'Join Queue'
              )}
            </Button>
          </div>
        </form>

        <p className="text-center text-slate-500 mt-6 text-lg">
          * Required fields
        </p>
      </div>

      {/* Koyo Branding */}
      <KoyoBranding className="py-6" />
    </div>
  );
}
